import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import './bottom-nav.css';

/**
 * BottomNav — mobile tab bar matching the W00/W01 prototype. Fixed to the
 * bottom of the buyer screens (Home, Feed). Protected destinations redirect to
 * login when the user isn't authenticated (handled by ProtectedRoute).
 */
interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  match?: (path: string) => boolean;
}

const ICON = {
  home: <path d="M3 12l9-9 9 9M9 21v-6h6v6M5 12v9h14v-9" />,
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </>
  ),
  heart: (
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
  ),
  bell: (
    <>
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </>
  ),
  user: (
    <>
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  car: (
    <>
      <path d="M3 13l2-5.4A2 2 0 016.9 6.3h10.2A2 2 0 0119 7.6L21 13v5h-2v-2H5v2H3v-5z" />
      <circle cx="7.5" cy="15.5" r="1.3" />
      <circle cx="16.5" cy="15.5" r="1.3" />
    </>
  ),
};

/** A seller-publishing path (/my-listings, /dealer, /publish, /vehicles/:id/publish). */
const isSellerPath = (p: string) =>
  p.startsWith('/my-listings') || p.startsWith('/dealer') || p.includes('/publish');

const ITEMS: NavItem[] = [
  { to: '/', label: 'Home', icon: ICON.home, match: (p) => p === '/' },
  {
    to: '/search',
    label: 'Explorar',
    icon: ICON.search,
    match: (p) => (p.startsWith('/search') || p.startsWith('/vehicles')) && !isSellerPath(p),
  },
  { to: '/my-listings', label: 'Vender', icon: ICON.car, match: isSellerPath },
  { to: '/saved', label: 'Guardados', icon: ICON.heart },
  { to: '/my-contacts', label: 'Actividad', icon: ICON.bell },
  { to: '/profile', label: 'Yo', icon: ICON.user },
];

export const BottomNav: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <nav className="cia-bottomnav" aria-label="Navegación principal">
      <div className="cia-bottomnav-inner">
        {ITEMS.map((item) => {
          const active = item.match ? item.match(pathname) : pathname === item.to;
          return (
            <button
              key={item.to}
              type="button"
              className={`cia-navitem${active ? ' cia-navitem--active' : ''}`}
              aria-current={active ? 'page' : undefined}
              onClick={() => navigate(item.to)}
            >
              <span className="cia-navitem-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {item.icon}
                </svg>
              </span>
              <span className="cia-navitem-label">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
